class ProductBundleStepped extends HTMLElement {
  constructor() {
    super();

    this.template;
    this.bundleCount = 0;
    // Store original empty states for each group
    this.groupEmptyStates = new Map();

    // Wait for theme to be available before setting up event listeners
    this.initializeWhenReady();
  }

  initializeWhenReady() {
    if (typeof window.theme !== 'undefined' && window.theme.utils) {
      this.captureGroupEmptyStates();
      this.setupEventListeners();
      
      // Initialize progress bar on page load
      if (this.showProgressBar) {
        this.updateProgressBar();
      }
      
      // Initialize sidebar height management for desktop
      this.setupSidebarHeightManagement();
    } else {
      // Wait for theme to load
      setTimeout(() => this.initializeWhenReady(), 50);
    }
  }

  captureGroupEmptyStates() {
    // Find all sidebar group sections and capture their initial empty state
    const groups = this.querySelectorAll('.product-bundle__item-group[data-group-id]');
    groups.forEach(group => {
      const groupId = group.getAttribute('data-group-id');
      const emptyVariant = group.querySelector('[data-product-bundle-variant][available]');
      if (emptyVariant && groupId) {
        // Clone the entire empty variant element
        this.groupEmptyStates.set(groupId, emptyVariant.cloneNode(true));
      }
    });
  }

  setupEventListeners() {
    this.addEventListener('bundle:added', this.onAddHandler.bind(this));
    this.addEventListener('bundle:removed', this.onRemoveHandler.bind(this));
    this.addEventListener('change', this.onChangeHandler.bind(this));
    this.addEventListener('input', this.onInputHandler.bind(this));
    this.submitButton.addEventListener('click', this.onSubmitHandler.bind(this));
    
    // Handle "next step" buttons that advance to the next tab
    this.setupNextStepButtons();
    
    // Handle empty state buttons that navigate to their corresponding tabs
    this.setupEmptyStateButtons();

  }

  /**
   * Sets up event listeners for "next step" buttons that advance to the next tab panel.
   * These buttons are rendered within each tab panel and update the main tabs-element.
   * Uses event delegation to handle clicks on buttons that may be hidden or dynamically updated.
   */
  setupNextStepButtons() {
    // Find the main tabs-element that controls all tab panels
    const mainTabsElement = this.querySelector('tabs-element');
    if (!mainTabsElement) return;

    // Use event delegation on the entire component to catch clicks on any "next step" button
    this.addEventListener('click', (event) => {
      // Find the closest button with data-next-step attribute
      const button = event.target.closest('[data-next-step]');
      
      if (button) {
        const nextStepIndex = parseInt(button.getAttribute('data-next-step'));
        if (!isNaN(nextStepIndex)) {
          // Update the main tabs-element's selectedIndex to show the next tab
          mainTabsElement.selectedIndex = nextStepIndex;
          
          // Scroll user back to the top of the section
          this.scrollToSection();
        }
      }
    });
  }

  /**
   * Sets up event listeners for empty state buttons in the bundle summary.
   * These buttons navigate to the corresponding tab when clicked, allowing users
   * to quickly jump to a section to add products.
   */
  setupEmptyStateButtons() {
    // Find the main tabs-element that controls all tab panels
    const mainTabsElement = this.querySelector('tabs-element');
    if (!mainTabsElement) return;

    // Use event delegation to handle clicks on empty state clickable elements
    this.addEventListener('click', (event) => {
      // Removing an item empties its slot before this click finishes bubbling,
      // so clicks from the item's own controls must not count as clicking the slot
      if (event.target.closest('product-bundle-remove-button, quantity-input')) return;

      // Check if the click was on an empty state clickable element
      const emptyStateElement = event.target.closest('[data-empty-state-clickable]');
      
      if (emptyStateElement && emptyStateElement.hasAttribute('available')) {
        // Only navigate if the element still has the 'available' attribute (is empty)
        const targetTabIndex = parseInt(emptyStateElement.getAttribute('data-target-tab-index'));
        
        if (!isNaN(targetTabIndex)) {
          // Update the main tabs-element's selectedIndex to show the target tab
          mainTabsElement.selectedIndex = targetTabIndex;
          
          // Scroll to the section
          this.scrollToSection();
        }
      }
    });
    
    // Set up a MutationObserver to update cursor/hover styles when available attribute changes
    this.updateEmptyStateStyles();
  }

  /**
   * Updates the visual styles of empty state elements based on whether they're available (empty).
   * Adds cursor-pointer and hover styles to empty states, removes them from filled states.
   */
  updateEmptyStateStyles() {
    const emptyStateElements = this.querySelectorAll('[data-empty-state-clickable]');
    emptyStateElements.forEach(element => this.setupEmptyStateStylesForElement(element));
  }

  /**
   * Sets up intelligent scroll behavior for the sidebar.
   * When hovering over the sidebar, mouse wheel scrolls the sidebar content if it has overflow,
   * otherwise allows the main page to scroll.
   * @param {HTMLElement} sidebarElement - The sidebar element to apply scroll behavior to
   */
  setupSidebarScrollBehavior(sidebarElement) {
    const handleWheel = (event) => {
      // Only apply on desktop
      if (window.innerWidth <= 1024) return;
      
      const scrollTop = sidebarElement.scrollTop;
      const scrollHeight = sidebarElement.scrollHeight;
      const clientHeight = sidebarElement.clientHeight;
      const delta = event.deltaY;
      
      // Check if sidebar has scrollable content
      const hasScrollableContent = scrollHeight > clientHeight;
      
      if (!hasScrollableContent) {
        // No overflow, allow page scroll
        return;
      }
      
      // Check if we're at the boundaries
      const isAtTop = scrollTop === 0;
      const isAtBottom = Math.abs(scrollHeight - clientHeight - scrollTop) < 1;
      
      // Prevent page scroll only when:
      // - Scrolling down and not at bottom, OR
      // - Scrolling up and not at top
      if ((delta > 0 && !isAtBottom) || (delta < 0 && !isAtTop)) {
        event.preventDefault();
        event.stopPropagation();
        
        // Manually scroll the sidebar
        sidebarElement.scrollTop += delta;
      }
      // Otherwise, allow the event to bubble and scroll the page
    };
    
    // Add wheel event listener
    sidebarElement.addEventListener('wheel', handleWheel, { passive: false });
    
    // Store cleanup function
    this._cleanupSidebarScroll = () => {
      sidebarElement.removeEventListener('wheel', handleWheel);
    };
  }

  /**
   * Sets up dynamic sidebar height management based on viewport visibility.
   * Ensures the sticky sidebar never overflows below the viewport on desktop (>1024px).
   */
  setupSidebarHeightManagement() {
    const sidebarElement = this.querySelector('sticky-element');
    if (!sidebarElement) return;
    
    // Add smooth transition for height changes and ensure overflow is handled
    sidebarElement.style.transition = 'max-height 0.3s ease-out';
    sidebarElement.style.overflowY = 'auto';
    
    // Set up intelligent scroll handling
    this.setupSidebarScrollBehavior(sidebarElement);
    
    // Use requestAnimationFrame for smoother updates
    let ticking = false;
    let lastKnownScrollPosition = 0;
    
    const updateSidebarHeight = () => {
      // Only apply on desktop viewports (>1024px)
      if (window.innerWidth <= 1024) {
        sidebarElement.style.maxHeight = '';
        sidebarElement.style.transition = '';
        sidebarElement.style.overflowY = '';
        return;
      }
      
      // Get sticky header height (CSS variable)
      const stickyHeaderHeight = parseInt(
        getComputedStyle(document.documentElement)
          .getPropertyValue('--sticky-header-height') || '0'
      );
      
      // Calculate buffer space at bottom
      const bufferSpace = 50;
      
      // Simple calculation: viewport height minus header minus buffer
      // This ensures the sidebar ALWAYS fits in the viewport
      const maxHeight = window.innerHeight - stickyHeaderHeight - bufferSpace;
      
      // Round to nearest 10px to reduce jitter
      const roundedHeight = Math.round(maxHeight / 10) * 10;
      
      // Apply the calculated max height with !important via setProperty
      sidebarElement.style.setProperty('max-height', `${roundedHeight}px`, 'important');
      
      ticking = false;
    };
    
    // Update on scroll with requestAnimationFrame for smooth 60fps updates
    const handleScroll = () => {
      lastKnownScrollPosition = window.scrollY;
      
      if (!ticking) {
        window.requestAnimationFrame(() => {
          updateSidebarHeight();
        });
        ticking = true;
      }
    };
    
    // Update on resize with debouncing
    let resizeTimeout;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(updateSidebarHeight, 100);
    };
    
    // Set up event listeners
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleResize);
    
    // Initial calculation
    updateSidebarHeight();
    
    // Store cleanup function for potential future use
    this._cleanupSidebarHeight = () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
      sidebarElement.style.removeProperty('max-height');
      sidebarElement.style.transition = '';
      sidebarElement.style.overflowY = '';
    };
  }

  /**
   * Smoothly scrolls the page to the top of the nearest parent .shopify-section element.
   * This ensures users see the new tab content after clicking a "next step" button.
   * Includes a delay to allow the tab transition to start, and scrolls 50px above the section.
   */
  scrollToSection() {
    // Find the nearest parent shopify section
    const section = this.closest('.shopify-section');
    
    if (section) {
      // Add a small delay to allow the tab transition animation to begin
      setTimeout(() => {
        // Calculate the target scroll position (50px above the section top)
        const sectionTop = section.getBoundingClientRect().top + window.pageYOffset;
        const targetPosition = sectionTop - 50;
        
        // Smoothly scroll to the calculated position
        window.scrollTo({
          top: targetPosition,
          behavior: 'smooth'
        });
      }, 150);
    }
  }


  get bundleMin() {
    return this.hasAttribute('data-minimum') ? parseInt(this.getAttribute('data-minimum')) || 0 : 0;
  }

  get bundleMax() {
    const minimum = this.bundleMin;
    const maximum = this.hasAttribute('data-maximum') ? parseInt(this.getAttribute('data-maximum')) : 3;
    return maximum > minimum ? maximum : minimum;
  }

  get variants() {
    return Array.from(this.querySelectorAll('[data-product-bundle-variant]'));
  }

  get defaultVariants() {
    return Array.from(this.querySelectorAll('[data-product-bundle-default-variant]'));
  }

  get submitButton() {
    return this.querySelector('[data-product-bundle-submit]');
  }

  get cartDrawer() {
    return document.querySelector('cart-drawer');
  }

  get preventDuplicate() {
    return this.hasAttribute('data-prevent-duplicate');
  }

  get groups() {
    // Return the motion-list elements which represent each group
    return Array.from(this.querySelectorAll('motion-list[data-group-id]'));
  }

  getGroupPreventDuplicates(groupId) {
    // Query the motion-list element for the group settings
    const group = this.querySelector(`motion-list[data-group-id="${groupId}"]`);
    return group ? group.getAttribute('data-prevent-duplicates') === 'true' : false;
  }

  getGroupMinQuantity(groupId) {
    // Query the motion-list element for the group settings
    const group = this.querySelector(`motion-list[data-group-id="${groupId}"]`);
    if (group) {
      const minQty = group.getAttribute('data-minimum-quantity');
      return minQty !== null ? parseInt(minQty) || 0 : 1;
    }
    return 1;
  }

  getGroupMaxQuantity(groupId) {
    // Query the motion-list element for the group settings
    const group = this.querySelector(`motion-list[data-group-id="${groupId}"]`);
    return group ? parseInt(group.getAttribute('data-maximum-quantity')) || 999 : 999;
  }

  getGroupCurrentQuantity(groupId) {
    // Query specifically within the sidebar for this group's section
    const groupSection = this.querySelector(`.product-bundle__item-group[data-group-id="${groupId}"]`);
    if (!groupSection) {
      console.warn(`Group section not found for groupId: ${groupId}`);
      return 0;
    }
    
    let totalQuantity = 0;
    // Only get variants that are direct descendants of this specific group section
    const groupVariants = Array.from(groupSection.querySelectorAll('[data-product-bundle-variant]'));
    
    groupVariants.forEach((variant, index) => {
      // Only count variants that are not in "available" (empty) state
      if (!variant.hasAttribute('available')) {
        const quantityInput = variant.querySelector('quantity-input .quantity__input');
        const quantity = quantityInput ? parseInt(quantityInput.value) || 1 : 1;
        totalQuantity += quantity;
      }
    });
    
    return totalQuantity;
  }

  getProductGroup(product) {
    // The group ID is stored on the product-card-info element inside the product card
    const productCardInfo = product.querySelector('product-card-info');
    return productCardInfo ? productCardInfo.getAttribute('data-group-id') : null;
  }

  updateGroupButtonStates(groupId) {
    const currentQuantity = this.getGroupCurrentQuantity(groupId);
    const maxQuantity = this.getGroupMaxQuantity(groupId);
    const preventDuplicates = this.getGroupPreventDuplicates(groupId);
        
    // Update the visual quantity counter if it exists
    this.updateGroupQuantityDisplay(groupId, currentQuantity, maxQuantity);
    
    // Find all product cards in this group's motion-list
    const motionList = this.querySelector(`motion-list[data-group-id="${groupId}"]`);
    if (!motionList) return;
    
    const groupProducts = motionList.querySelectorAll('.product-card');
    
    groupProducts.forEach((product, index) => {
      const submitButton = product.querySelector('.product-form__submit');
      if (submitButton) {
        const currentLockReason = product.getAttribute('data-locked-reason');
        
        // Check if product should be locked due to quantity limits
        const shouldLockForQuantity = currentQuantity >= maxQuantity;
        
        // Check if product should be locked due to prevent duplicates
        const shouldLockForDuplicates = preventDuplicates && product.hasAttribute('locked') && currentLockReason === 'duplicates';
        
        if (shouldLockForQuantity) {
          // Lock due to quantity limits
          submitButton.setAttribute('aria-disabled', 'true');
          product.setAttribute('locked', '');
          product.setAttribute('data-locked-reason', 'quantity');
        } else if (shouldLockForDuplicates) {
          // Keep locked due to prevent duplicates
          submitButton.setAttribute('aria-disabled', 'true');
          // Keep existing locked state and reason
        } else {
          // Unlock the product (remove quantity locks, but preserve duplicate locks if they exist)
          if (currentLockReason === 'quantity' || !currentLockReason) {
            submitButton.removeAttribute('aria-disabled');
            product.removeAttribute('locked');
            product.removeAttribute('data-locked-reason');
          }
        }
      }
    });
    
    // Update quantity input max values for all variants in this group
    this.updateGroupQuantityInputLimits(groupId);
  }

  updateGroupQuantityDisplay(groupId, currentQuantity, maxQuantity) {
    // Find the group section in the sidebar
    const groupSection = this.querySelector(`.product-bundle__item-group[data-group-id="${groupId}"]`);
    if (!groupSection) return;
    
    // Look for the quantity display element
    const quantityDisplay = groupSection.querySelector('.product-bundle__item-group-quantity');
    if (quantityDisplay) {
      // Update the text content with current/max format
      quantityDisplay.textContent = `${currentQuantity}`;
    }
  }

  updateProductCardAddedState(product, variantId) {
    // Find the group this product belongs to
    const groupId = this.getProductGroup(product);
    
    if (!groupId) return;
    
    // Get the quantity of this specific variant in the bundle for this group
    const variantQuantity = this.getVariantQuantityInGroup(variantId, groupId);
    
    // Find the submit button and its text element
    const submitButton = product.querySelector('.product-form__submit');
    if (!submitButton) return;
    
    const btnText = submitButton.querySelector('.btn-text');
    if (!btnText) return;
    
    // Store the original button text if we haven't already
    if (!submitButton.hasAttribute('data-original-text')) {
      submitButton.setAttribute('data-original-text', btnText.textContent.trim());
    }
    
    if (variantQuantity > 0) {
      // Update button text to show quantity added
      btnText.textContent = `${variantQuantity} in bundle`;
      
      // Change button style from primary to secondary when added to bundle
      submitButton.classList.remove('button--secondary');
      submitButton.classList.add('button--primary');
    } else {
      // Restore original button text
      const originalText = submitButton.getAttribute('data-original-text');
      if (originalText) {
        btnText.textContent = originalText;
      }
      
      // Change button style back to primary when removed from bundle
      submitButton.classList.remove('button--primary');
      submitButton.classList.add('button--secondary');
    }
  }

  getVariantQuantityInGroup(variantId, groupId) {
    // Find the group section in the sidebar
    const groupSection = this.querySelector(`.product-bundle__item-group[data-group-id="${groupId}"]`);
    if (!groupSection) return 0;
    
    // Find all variants with this variant ID in this group
    const variants = Array.from(groupSection.querySelectorAll('[data-product-bundle-variant]'));
    let totalQuantity = 0;
    
    variants.forEach(variant => {
      if (!variant.hasAttribute('available') && variant.getAttribute('data-variant-id') === variantId.toString()) {
        const quantityInput = variant.querySelector('quantity-input .quantity__input');
        const quantity = quantityInput ? parseInt(quantityInput.value) || 1 : 1;
        totalQuantity += quantity;
      }
    });
    
    return totalQuantity;
  }

  updateAllProductCardStates(groupId) {
    // Find all product cards in this group
    const motionList = this.querySelector(`motion-list[data-group-id="${groupId}"]`);
    if (!motionList) return;
    
    const groupProducts = motionList.querySelectorAll('.product-card');
    
    groupProducts.forEach(product => {
      const form = product.querySelector('form[is="product-bundle-form"]');
      if (form) {
        const variant = form.getSelectedVariant?.();
        if (variant) {
          this.updateProductCardAddedState(product, variant.id);
        }
      }
    });
  }

  updateGroupQuantityInputLimits(groupId) {
    const currentQuantity = this.getGroupCurrentQuantity(groupId);
    const maxQuantity = this.getGroupMaxQuantity(groupId);
    
    // Find all variants in this group within the sidebar - use specific class selector
    const groupSection = this.querySelector(`.product-bundle__item-group[data-group-id="${groupId}"]`);
    if (!groupSection) return;
    
    const variants = groupSection.querySelectorAll('[data-product-bundle-variant]:not([available])');
    
    variants.forEach(variant => {
      const quantityInput = variant.querySelector('quantity-input');
      if (quantityInput) {
        const input = quantityInput.querySelector('.quantity__input');
        if (input) {
          const currentItemQuantity = parseInt(input.value) || 1;
          const otherItemsQuantity = currentQuantity - currentItemQuantity;
          const maxAllowedForThisItem = maxQuantity - otherItemsQuantity;
          
          // Set the max attribute to the maximum this item can have
          input.setAttribute('max', Math.max(1, maxAllowedForThisItem));
          input.setAttribute('data-max', Math.max(1, maxAllowedForThisItem));
          
          // Trigger validation to update button states
          if (quantityInput.validateQtyRules) {
            quantityInput.validateQtyRules();
          }
        }
      }
    });
  }

  checkGroupQuantityLimits(groupId) {
    const currentQuantity = this.getGroupCurrentQuantity(groupId);
    const minQuantity = this.getGroupMinQuantity(groupId);
    const maxQuantity = this.getGroupMaxQuantity(groupId);
    
    return {
      current: currentQuantity,
      min: minQuantity,
      max: maxQuantity,
      canAddMore: currentQuantity < maxQuantity,
      meetsMinimum: currentQuantity >= minQuantity,
      atMaximum: currentQuantity >= maxQuantity
    };
  }

  validateGroupMinimums() {
    // Get all unique group IDs from the motion-list elements
    const motionLists = Array.from(this.querySelectorAll('motion-list[data-group-id]'));
    
    for (const motionList of motionLists) {
      const groupId = motionList.getAttribute('data-group-id');
      const limits = this.checkGroupQuantityLimits(groupId);
      
      if (!limits.meetsMinimum) {
        // Show error message for this group
        this.handleErrorMessage(`Please add at least ${limits.min} item(s) to complete your selection.`);
        return false;
      }
    }
    
    return true;
  }

  updateSubmitButtonState() {
    // For stepped bundles, only check group-level minimums - the delivery date
    // is chosen once for the whole order in the cart
    // Each motion-list element defines its own minimum/maximum requirements
    const allProductRequirementsMet = this.checkAllGroupMinimums();

    this.submitButton.disabled = !allProductRequirementsMet;
    this.submitButton.toggleAttribute('aria-disabled', !allProductRequirementsMet);
  }

  checkAllGroupMinimums() {
    // Query motion-list elements to get all group IDs
    const motionLists = Array.from(this.querySelectorAll('motion-list[data-group-id]'));
    
    for (const motionList of motionLists) {
      const groupId = motionList.getAttribute('data-group-id');
      const current = this.getGroupCurrentQuantity(groupId);
      const min = this.getGroupMinQuantity(groupId);
      
      if (current < min) {
        return false;
      }
    }
    
    return true;
  }

  get showProgressBar() {
    return this.hasAttribute('data-show-progress-bar');
  }

  get template() {
    return this._template = this._template || this.querySelector('[data-product-bundle-variant][available]').cloneNode(true);
  }

  onInputHandler(event) {
    // Validate quantity inputs as the user types
    if (event && event.target && event.target.classList.contains('quantity__input')) {
      this.validateQuantityInput(event.target);
    }
  }

  onChangeHandler(event) {
    // Check if the change event is from a quantity input
    if (event && event.target && (event.target.classList.contains('quantity__input') || event.target.closest('quantity-input'))) {
      const input = event.target.classList.contains('quantity__input') ? event.target : event.target.querySelector('.quantity__input');
      
      // Validate and constrain the input value
      if (input) {
        this.validateQuantityInput(input);
      }
      
      // Find which group this quantity input belongs to
      const variantElement = event.target.closest('[data-product-bundle-variant]');
      if (variantElement) {
        const groupSection = variantElement.closest('[data-group-id]');
        if (groupSection) {
          const groupId = groupSection.getAttribute('data-group-id');
          
          // Update button states for this group
          this.updateGroupButtonStates(groupId);
          
          // Update all product card states for this group (quantities may have changed)
          this.updateAllProductCardStates(groupId);
        }
      }
    }
    
    // Always update totals, progress bar, and submit button state
    this.updateTotal();
    this.updateTotalWithCurrency();
    this.updateSubmitButtonState();
    if (this.showProgressBar) {
      this.updateProgressBar();
    }
  }

  /**
   * Validates and constrains a quantity input value to respect min, max, and step attributes.
   * @param {HTMLInputElement} input - The quantity input element to validate
   */
  validateQuantityInput(input) {
    if (!input) return;
    
    let value = parseInt(input.value) || 0;
    const min = parseInt(input.getAttribute('data-min') || input.min) || 1;
    const max = parseInt(input.getAttribute('data-max') || input.max) || 999;
    const step = parseInt(input.step) || 1;
    
    // Constrain to min/max
    if (value < min) {
      value = min;
    } else if (value > max) {
      value = max;
    }
    
    // Ensure value respects step
    if (value % step !== 0) {
      value = Math.floor(value / step) * step;
      if (value < min) value = min;
    }
    
    // Update the input if value changed
    if (parseInt(input.value) !== value) {
      input.value = value;
    }
  }


  onSubmitHandler(event) {
    const validateQuantity = this.variants
      .filter(variant => !variant.hasAttribute('available'))
      .every((variant) => {
        const quantityInputElement = variant.querySelector('quantity-input');
        if (!quantityInputElement) return true; // Skip if no quantity input
        
        const target = quantityInputElement.querySelector('.quantity__input');
        if (!target) return true; // Skip if no input found
        
        const inputValue = parseInt(target.value);
        let message = '';

        if (inputValue < parseInt(target.getAttribute('data-min'))) {
          message = theme.quickOrderListStrings.minError.replace('[min]', target.getAttribute('data-min'));
        }
        else if (inputValue > parseInt(target.max)) {
          message = theme.quickOrderListStrings.maxError.replace('[max]', target.max);
        }
        else if (inputValue % parseInt(target.step) !== 0) {
          message = theme.quickOrderListStrings.stepError.replace('[step]', target.step);
        }

        if (message) {
          target.setCustomValidity(message);
          target.reportValidity();
          target.value = target.defaultValue;
          target.select();

          return false;
        }

        return true;
      });
    if (!validateQuantity) return;

    // Validate group minimums
    if (!this.validateGroupMinimums()) {
      return;
    }

    // The bundle's products nest under the default (base) product in the cart.
    // A unique id ties every line of this kit together (and keeps a second kit
    // from merging into the first and gathering both kits' products)
    const parentVariantId = this.defaultVariants.length === 1
      ? this.defaultVariants[0].getAttribute('data-variant-id')
      : null;
    const bundleId = `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;

    // Build properties object for all items
    const buildProperties = () => ({
      '_bundle-builder': this.getAttribute('data-bundle-property-value'),
      '_bundle_id': bundleId
    });
    
    // Collect selected variants from the bundle
    const selectedVariants = this.variants
      .filter(variant => !variant.hasAttribute('available'))
      .map(variant => {
        const quantityInput = variant.querySelector('quantity-input .quantity__input');
        const quantity = quantityInput ? parseInt(quantityInput.value) || 1 : 1;
        return {
          id: variant.getAttribute('data-variant-id'),
          quantity: quantity,
          properties: buildProperties(),
          ...(parentVariantId && { parent_id: parentVariantId })
        };
      });

    // Collect default/required variants (e.g., the base product)
    const defaultVariantItems = this.defaultVariants.map(variant => {
      const quantityInput = variant.querySelector('quantity-input .quantity__input');
      const quantity = quantityInput ? parseInt(quantityInput.value) || 1 : 1;
      return {
        id: variant.getAttribute('data-variant-id'),
        quantity: quantity,
        properties: buildProperties()
      };
    });

    // Combine both arrays into the items to add to cart
    const data = {
      items: [...defaultVariantItems, ...selectedVariants]
    };

    if (document.body.classList.contains('template-cart') || theme.settings.cartType === 'page') {
      theme.utils.postLink2(theme.routes.cart_add_url, {
        parameters: {
          ...data
        }
      });
      return;
    }
    
    event.preventDefault();
    if (this.submitButton.hasAttribute('aria-disabled')) return;
    this.activeElement = event.submitter || event.currentTarget;

    this.handleErrorMessage();

    let sectionsToBundle = [];
    document.documentElement.dispatchEvent(new CustomEvent('cart:bundled-sections', { bubbles: true, detail: { sections: sectionsToBundle } }));
    
    const body = JSON.stringify({
      ...data,
      sections: sectionsToBundle,
      sections_url: window.location.pathname
    });

    this.submitButton.setAttribute('aria-disabled', 'true');
    this.submitButton.setAttribute('aria-busy', 'true');

    fetch(`${theme.routes.cart_add_url}`, { ...theme.utils.fetchConfig('javascript'), body })
      .then((response) => response.json())
      .then(async (parsedState) => {
        if (parsedState.status) {
          this.handleErrorMessage(parsedState.description);
          document.dispatchEvent(new CustomEvent('ajaxProduct:error', {
            detail: {
              errorMessage: parsedState.description
            }
          }));
          
          const submitButtonText = this.submitButton.querySelector('.btn-text>span');
          if (!submitButtonText || !submitButtonText.hasAttribute('data-sold-out')) return;
          submitButtonText.innerText = submitButtonText.getAttribute('data-sold-out');
          this.submitButton.setAttribute('aria-disabled', 'true');
          this.error = true;
          return;
        }

        const cartJson = await (await fetch(theme.routes.cart_url, { ...theme.utils.fetchConfig('json', 'GET')})).json();
        cartJson['sections'] = parsedState['sections'];

        theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'product-bundle', cart: cartJson });
        document.dispatchEvent(new CustomEvent('ajaxProduct:added', {
          detail: {
            product: parsedState
          }
        }));

        this.cartDrawer?.show(this.activeElement);
        this.resetVariants();
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        this.submitButton.removeAttribute('aria-busy');

        if (!this.error) {
          this.submitButton.removeAttribute('aria-disabled');
        }
      });
  }

  handleErrorMessage(errorMessage = false) {
    if (this.hideErrors) return;
    
    this.errorMessage = this.errorMessage || this.querySelector('.product-form__error-message');
    if (!this.errorMessage) return;

    this.errorMessage.toggleAttribute('hidden', !errorMessage);
    this.errorMessage.innerText = errorMessage;
  }

  onAddHandler(event) {
    this.addVariant(event.detail.product, event.detail.variant);
    this.updateTotal();
    this.updateTotalWithCurrency();
    if (this.showProgressBar) {
      this.updateProgressBar();
    }
    
    // Ensure the submit button's busy state is cleared after DOM updates
    const submitButton = event.detail.product.querySelector('.product-form__submit');
    if (submitButton) {
      // Use requestAnimationFrame to ensure this runs after any pending DOM updates
      requestAnimationFrame(() => {
        submitButton.removeAttribute('aria-busy');
      });
    }
    
    // Update the product card to show it's been added
    this.updateProductCardAddedState(event.detail.product, event.detail.variant.id);
  }

  onRemoveHandler(event) {
    const variantId = event.detail.variant.getAttribute('data-variant-id');
    const product = event.detail.variant.product;
    
    this.clearVariant(event.detail.variant);
    this.reorderVariants();
    this.updateTotal();
    this.updateTotalWithCurrency();
    if (this.showProgressBar) {
      this.updateProgressBar();
    }
    
    // Update the product card state after removal
    if (product && variantId) {
      this.updateProductCardAddedState(product, variantId);
    }
  }

  updateTotal() {
    let subtotal = 0;
    this.variants.forEach((variant) => {
      if (!variant.hasAttribute('available')) {
        const price = variant.querySelector('[data-product-bundle-variant-price]');
        const quantityInput = variant.querySelector('quantity-input .quantity__input');
        const quantity = quantityInput ? parseInt(quantityInput.value) || 1 : 1;
        subtotal += parseInt(price.getAttribute('data-price')) * quantity;
      }
    });

    const element = this.querySelector('[data-product-bundle-total]');
    // Get the starting cost and add it to the subtotal
    const startingCost = parseInt(element.getAttribute('data-product-bundle-starting-cost')) || 0;
    const totalWithStartingCost = subtotal + startingCost;
    
    // Apply discount if applicable
    const discountedTotal = this.applyActiveDiscount(totalWithStartingCost);
    
    element.innerHTML = theme.Currency.formatMoney(discountedTotal, theme.settings.moneyFormat);
  }

  updateTotalWithCurrency() {
    let subtotal = 0;
    this.variants.forEach((variant) => {
      if (!variant.hasAttribute('available')) {
        const price = variant.querySelector('[data-product-bundle-variant-price]');
        const quantityInput = variant.querySelector('quantity-input .quantity__input');
        const quantity = quantityInput ? parseInt(quantityInput.value) || 1 : 1;
        subtotal += parseInt(price.getAttribute('data-price')) * quantity;
      }
    });

    const element = this.querySelector('[data-product-bundle-total-with-currency]');
    if (!element) return;

    // Get the starting cost and add it to the subtotal
    const startingCost = parseInt(element.getAttribute('data-product-bundle-starting-cost')) || 0;
    const totalWithStartingCost = subtotal + startingCost;
    
    // Apply discount if applicable
    const discountedTotal = this.applyActiveDiscount(totalWithStartingCost);
    
    element.innerHTML = theme.Currency.formatMoney(discountedTotal, theme.settings.moneyFormat);
  }

  /**
   * Calculates the discounted total based on active discount tier.
   * @param {Number} total - Total amount in cents
   * @returns {Number} Discounted total in cents
   */
  applyActiveDiscount(total) {
    const progressBar = this.querySelector('[data-product-bundle-progress-bar]');
    if (!progressBar) return total;
    
    const totalInCurrency = total / 100;
    
    // Get all tier thresholds
    const tiers = [];
    for (let i = 1; i <= 5; i++) {
      const threshold = progressBar.getAttribute(`data-tier-${i}-threshold`);
      const percentage = progressBar.getAttribute(`data-tier-${i}-percentage`);
      if (threshold && percentage) {
        tiers.push({
          threshold: parseFloat(threshold),
          percentage: parseFloat(percentage)
        });
      }
    }
    
    // Sort tiers by threshold (lowest first)
    tiers.sort((a, b) => a.threshold - b.threshold);
    
    // Find the highest applicable tier
    let activeTier = null;
    for (const tier of tiers) {
      if (totalInCurrency >= tier.threshold) {
        activeTier = tier;
      }
    }
    
    // Apply discount if a tier is active
    if (activeTier) {
      const discountAmount = Math.round(total * (activeTier.percentage / 100));
      return total - discountAmount;
    }
    
    return total;
  }

  /**
   * Updates the progress bar to show discount tier progress.
   * Calculates current bundle total and shows progress toward the next discount tier.
   */
  updateProgressBar() {
    const progressBar = this.querySelector('[data-product-bundle-progress-bar]');
    if (!progressBar) return;
    
    // Get the current bundle total (in cents)
    let subtotal = 0;
    this.variants.forEach((variant) => {
      if (!variant.hasAttribute('available')) {
        const price = variant.querySelector('[data-product-bundle-variant-price]');
        const quantityInput = variant.querySelector('quantity-input .quantity__input');
        const quantity = quantityInput ? parseInt(quantityInput.value) || 1 : 1;
        subtotal += parseInt(price.getAttribute('data-price')) * quantity;
      }
    });
    
    // Add starting cost (default product)
    const totalElement = this.querySelector('[data-product-bundle-total]');
    const startingCost = totalElement ? parseInt(totalElement.getAttribute('data-product-bundle-starting-cost')) || 0 : 0;
    const currentTotal = subtotal + startingCost;
    const currentTotalInCurrency = currentTotal / 100; // Convert cents to currency
    
    console.log('Progress Bar Update:', {
      subtotal,
      startingCost,
      currentTotal,
      currentTotalInCurrency
    });
    
    // Get all tier thresholds from data attributes
    const tiers = [];
    for (let i = 1; i <= 5; i++) {
      const threshold = progressBar.getAttribute(`data-tier-${i}-threshold`);
      const percentage = progressBar.getAttribute(`data-tier-${i}-percentage`);
      if (threshold && percentage) {
        tiers.push({
          threshold: parseFloat(threshold),
          percentage: parseFloat(percentage),
          index: i
        });
      }
    }
    
    // Sort tiers by threshold (lowest first)
    tiers.sort((a, b) => a.threshold - b.threshold);
        
    if (tiers.length === 0) {
      // No tiers configured, hide progress
      progressBar.style.setProperty('--progress', '0%');
      return;
    }
    
    // Find current and next tier
    let currentTier = null;
    let nextTier = null;
    
    for (const tier of tiers) {
      if (currentTotalInCurrency >= tier.threshold) {
        currentTier = tier;
      } else if (!nextTier) {
        nextTier = tier;
        break;
      }
    }
        
    // Update progress bar and message
    const messageElement = this.querySelector('[data-bundle-progress-message]');
    const amountElement = this.querySelector('[data-bundle-progress-amount]');
    const discountInfoElement = this.querySelector('[data-bundle-discount-info]');
    const discountLabelElement = this.querySelector('[data-bundle-discount-label]');
    const discountSavingsElement = this.querySelector('[data-bundle-discount-savings]');
    
    if (currentTier && !nextTier) {
      // Maximum tier reached
      progressBar.style.setProperty('--progress', '100%');
      if (messageElement) {
        messageElement.textContent = `${currentTier.percentage}% discount unlocked!`;
      }
      
      // Update amount display to show the target (highest tier threshold)
      if (amountElement) {
        amountElement.textContent = theme.Currency.formatMoney(currentTier.threshold * 100, theme.settings.moneyFormat);
      }
      
      // Show active discount and savings
      this.updateDiscountInfo(discountInfoElement, discountLabelElement, discountSavingsElement, currentTier, currentTotal);
    } else if (nextTier) {
      const targetThreshold = nextTier.threshold;
      const previousThreshold = currentTier ? currentTier.threshold : 0;
      const rangeSize = targetThreshold - previousThreshold;
      const progressInRange = currentTotalInCurrency - previousThreshold;
      const percent = Math.min(100, Math.max(0, (progressInRange / rangeSize) * 100));
      
      progressBar.style.setProperty('--progress', `${percent}%`);
      
      if (messageElement) {
        const remaining = targetThreshold - currentTotalInCurrency;
        if (remaining > 0) {
          messageElement.textContent = `Spend ${theme.Currency.formatMoney(remaining * 100, theme.settings.moneyFormat)} more for ${nextTier.percentage}% off this kit`;
        } else {
          messageElement.textContent = `${nextTier.percentage}% discount unlocked!`;
        }
      }
      
      // Update amount display to show the target (next tier threshold)
      if (amountElement) {
        amountElement.textContent = theme.Currency.formatMoney(targetThreshold * 100, theme.settings.moneyFormat);
      }
      
      // Show active discount and savings if on a tier
      if (currentTier) {
        this.updateDiscountInfo(discountInfoElement, discountLabelElement, discountSavingsElement, currentTier, currentTotal);
      } else {
        // Hide discount info if no tier reached yet
        if (discountInfoElement) {
          discountInfoElement.hidden = true;
        }
      }
    } else {
      // No tier reached yet
      const targetThreshold = tiers[0].threshold;
      const percent = Math.min(100, (currentTotalInCurrency / targetThreshold) * 100);
      
      progressBar.style.setProperty('--progress', `${percent}%`);
      
      if (messageElement) {
        const remaining = targetThreshold - currentTotalInCurrency;
        messageElement.textContent = `Spend ${theme.Currency.formatMoney(remaining * 100, theme.settings.moneyFormat)} more for ${tiers[0].percentage}% off this kit`;
      }
      
      // Update amount display to show the target (first tier threshold)
      if (amountElement) {
        amountElement.textContent = theme.Currency.formatMoney(targetThreshold * 100, theme.settings.moneyFormat);
      }
      
      // Hide discount info if no tier reached yet
      if (discountInfoElement) {
        discountInfoElement.hidden = true;
      }
    }
  }

  /**
   * Updates the discount information display showing active discount and savings amount.
   * @param {HTMLElement} infoElement - Container element for discount info
   * @param {HTMLElement} labelElement - Element to show discount description
   * @param {HTMLElement} savingsElement - Element to show savings amount
   * @param {Object} tier - Current discount tier object with percentage
   * @param {Number} currentTotal - Current bundle total in cents
   */
  updateDiscountInfo(infoElement, labelElement, savingsElement, tier, currentTotal) {
    if (!infoElement || !labelElement || !savingsElement || !tier) return;
    
    // Calculate savings amount
    const savingsAmount = Math.round(currentTotal * (tier.percentage / 100));
    
    // Show the discount info
    infoElement.hidden = false;
    labelElement.textContent = `Discount (${tier.percentage}%)`;
    savingsElement.textContent = `- ${theme.Currency.formatMoney(savingsAmount, theme.settings.moneyFormat)}`;
  }

  addVariant(product, variant) {
    // Get the group ID to check if duplicates are allowed
    const groupId = this.getProductGroup(product);
    const preventDuplicates = groupId && this.getGroupPreventDuplicates(groupId);
    
    // Check group quantity limits before adding
    const groupLimits = this.checkGroupQuantityLimits(groupId);
    
    if (!groupLimits.canAddMore) {
      return;
    }
    
    // If duplicates are allowed, check if this variant already exists in the same group
    if (!preventDuplicates) {
      const existingVariant = this.findExistingVariantInGroup(variant.id, groupId);
      if (existingVariant) {
        // Check if incrementing would exceed group max
        const currentQuantity = this.getGroupCurrentQuantity(groupId);
        if (currentQuantity >= this.getGroupMaxQuantity(groupId)) {
          return;
        }
        
        // Increment quantity instead of creating a new item
        this.incrementVariantQuantity(existingVariant);
        // Update button states after incrementing
        this.updateGroupButtonStates(groupId);
        this.scrollSidebarToItem(existingVariant);
        return;
      }
    }

    this.bundleCount++;

    // Note: For stepped bundles, we don't lock the entire wrapper based on global counts
    // Each group manages its own locking based on group-specific min/max quantities

    // Find the available slot in the specific group section
    let variantElement = this.getAvailableVariantInGroup(groupId);
    if (variantElement === null) {
      // If no available slot in the group, create a new one
      variantElement = this.createNewVariantInGroup(groupId);
    }

    if (variantElement) {
      const preview_image = variant.featured_media ? variant.featured_media.preview_image.src : variant.default_featured_media;
      const media = variantElement.querySelector('[data-product-bundle-variant-media]');
      if (preview_image.length > 0) {
        media.innerHTML = `<img src="${this.getResizedImageSrc(preview_image, 180)}" srcset="${this.getResizedImageSrc(preview_image, 180)} 180w, ${this.getResizedImageSrc(preview_image, 360)} 360w, ${this.getResizedImageSrc(preview_image, 540)} 540w" loading="lazy" is="lazy-image" />`;
      }

      const content = variantElement.querySelector('[data-product-bundle-variant-content]');
      // Only update the product info section, preserve the quantity section
      const productInfoSection = content.querySelector('.flex.flex-col');
      if (productInfoSection) {
        productInfoSection.innerHTML = `
          <p class="text-sm">${product.querySelector('[data-product-bundle-title]').textContent}</p>
          ${(() => {
            const validOptions = variant.options.filter(option => option !== 'Default Title');
            return validOptions.length > 0 ? `
            <ul class="grid gap-1-5">
              ${validOptions.map(option => `<li class="text-xs text-opacity">${option}</li>`).join('')}
            </ul>
          ` : '';
          })()}
          <div class="price text-sm flex flex-wrap gap-1-5 font-bold" data-price="${variant.price}" data-product-bundle-variant-price>
            ${theme.Currency.formatMoney(variant.price, theme.settings.moneyFormat)}
          </div>
        `;
      }

      variantElement.product = product;
      variantElement.setAttribute('data-variant-id', variant.id);
      variantElement.removeAttribute('available');
      
      // Show quantity input immediately if group allows more than 1 quantity
      const groupMaxQuantity = this.getGroupMaxQuantity(groupId);
      if (groupMaxQuantity > 1) {
        const quantityInput = variantElement.querySelector('quantity-input');
        if (quantityInput) {
          quantityInput.removeAttribute('hidden');
        }
      }
      
      // Get the group ID from the product and check if prevent duplicates is enabled for that group
      const productGroupId = this.getProductGroup(product);
      
      if (productGroupId && this.getGroupPreventDuplicates(productGroupId)) {
        product.setAttribute('locked', '');
        product.setAttribute('data-locked-reason', 'duplicates');
        // Also disable the submit button to prevent form submission
        const submitButton = product.querySelector('.product-form__submit');
        if (submitButton) {
          submitButton.setAttribute('aria-disabled', 'true');
        }
      }
      
      // Update button states for the group after adding the variant
      this.updateGroupButtonStates(groupId);
      
      // Update submit button state after variant is fully added
      this.updateSubmitButtonState();
      this.scrollSidebarToItem(variantElement);
    }
  }

  /**
   * Scrolls the bundle sidebar to the group a product was just added to, when
   * the sidebar is too long to show everything. Keeps the group heading in view
   * where it can, otherwise the added item itself
   */
  scrollSidebarToItem(item) {
    const group = item?.closest('.product-bundle__item-group');
    if (!group) return;

    requestAnimationFrame(() => {
      // Desktop scrolls the whole sidebar, the list scrolls on its own otherwise
      const scrolls = (element) =>
        element.scrollHeight > element.clientHeight && /auto|scroll/.test(getComputedStyle(element).overflowY);

      let scroller = group.parentElement;
      while (scroller && scroller !== this && !scrolls(scroller)) {
        scroller = scroller.parentElement;
      }
      if (!scroller || scroller === this) return;

      const scrollerTop = scroller.getBoundingClientRect().top;
      const offsetOf = (element) => element.getBoundingClientRect().top - scrollerTop + scroller.scrollTop;
      const itemBottom = offsetOf(item) + item.offsetHeight;
      const top = Math.max(offsetOf(group), itemBottom - scroller.clientHeight);

      scroller.scrollTo({ top, behavior: 'smooth' });
    });
  }

  clearVariant(variant) {
    // Get the group ID from the variant's parent group section in the sidebar
    const groupSection = variant.closest('[data-group-id]');
    const groupId = groupSection ? groupSection.getAttribute('data-group-id') : null;
        
    // Handle prevent duplicates unlocking if needed
    if (variant.product && groupId && this.getGroupPreventDuplicates(groupId)) {
      // Only unlock if it was locked for duplicates, not for quantity limits
      if (variant.product.getAttribute('data-locked-reason') === 'duplicates') {
        variant.product.removeAttribute('locked');
        variant.product.removeAttribute('data-locked-reason');
        // Also re-enable the submit button
        const submitButton = variant.product.querySelector('.product-form__submit');
        if (submitButton) {
          submitButton.removeAttribute('aria-disabled');
        }
      }
    }
    
    // Check if this is the last product in the group BEFORE marking as available
    const isLastInGroup = this.isLastVariantInGroup(groupId, variant);
    
    variant.setAttribute('available', '');
    variant.removeAttribute('data-variant-id');
    variant.querySelector('[data-product-bundle-variant-media]').innerHTML = '';
    variant.querySelector('[data-product-bundle-variant-media]').classList.remove('loading');
    
    // Reset and hide the quantity input
    const quantityInput = variant.querySelector('quantity-input');
    if (quantityInput) {
      quantityInput.reset();
      quantityInput.setAttribute('hidden', '');
    }

    this.bundleCount--;

    if (this.bundleCount < this.bundleMin) {
      this.submitButton.disabled = true;
    }
    else {
      // Always remove the variant
      variant.remove();
      
      // If this was the last variant in the group, create a new skeleton variant
      if (isLastInGroup) {
        this.createSkeletonVariantForGroup(groupId);
      }
    }
    
    // Always update submit button state after any change
    this.updateSubmitButtonState();
    
    // Note: For stepped bundles, wrapper locking is not used
    // Each group manages its own button states independently
    
    // Update button states for the group after removing the variant
    if (groupId) {
      this.updateGroupButtonStates(groupId);
    }
  }

  isLastVariantInGroup(groupId, currentVariant) {
    if (!groupId) return false;
    
    // Find the group section in the sidebar - use specific class selector
    const groupSection = this.querySelector(`.product-bundle__item-group[data-group-id="${groupId}"]`);
    if (!groupSection) return false;
    
    // Count variants that are not available (i.e., have products) excluding the current one being cleared
    const activeVariants = groupSection.querySelectorAll('[data-product-bundle-variant]:not([available])');
    
    // If there's only 1 active variant (the one being cleared), this is the last one
    return activeVariants.length === 1;
  }


  createSkeletonVariantForGroup(groupId) {
    // Find the group section in the sidebar - use specific class selector
    const groupSection = this.querySelector(`.product-bundle__item-group[data-group-id="${groupId}"]`);
    if (!groupSection) return;

    // Get the stored empty state for this group
    const emptyState = this.groupEmptyStates.get(groupId);
    if (emptyState) {
      // Clone the stored empty state and append it
      const clonedEmptyState = emptyState.cloneNode(true);
      groupSection.appendChild(clonedEmptyState);
      
      // Set up empty state styles for the newly created element
      this.setupEmptyStateStylesForElement(clonedEmptyState);
    }
  }

  /**
   * Sets up the MutationObserver for a single empty state element.
   * @param {HTMLElement} element - The empty state element to observe
   */
  setupEmptyStateStylesForElement(element) {
    if (!element.hasAttribute('data-empty-state-clickable')) return;
    
    const observer = new MutationObserver(() => {
      if (element.hasAttribute('available')) {
        // Empty state - make it clickable
        element.classList.add('cursor-pointer', 'hover:bg-opacity-5', 'hover:bg-current');
      } else {
        // Filled state - remove clickable styles
        element.classList.remove('cursor-pointer', 'hover:bg-opacity-5', 'hover:bg-current');
      }
    });
    
    // Observe attribute changes on the element
    observer.observe(element, { attributes: true, attributeFilter: ['available'] });
    
    // Set initial state
    if (element.hasAttribute('available')) {
      element.classList.add('cursor-pointer', 'hover:bg-opacity-5', 'hover:bg-current');
    }
  }

  reorderVariants() {
    // For stepped bundles, we don't use CSS order property since each group
    // manages its items independently in natural DOM order
    // Items are added/removed within their specific group containers
    
    // Reset any order styling that might have been applied
    this.variants.forEach((variant) => {
      variant.style.order = '';
    });
  }

  resetVariants() {
    this.variants.forEach((variant) => {
      this.clearVariant(variant);
    });

    this.reorderVariants();
    this.updateTotal();
    this.updateTotalWithCurrency();
    if (this.showProgressBar) {
      this.updateProgressBar();
    }
  }

  getAvailableAvriant() {
    return this.querySelector('[data-product-bundle-variant][available]');
  }

  getAvailableVariantInGroup(groupId) {
    // Find the group section in the sidebar - use specific class selector
    const groupSection = this.querySelector(`.product-bundle__item-group[data-group-id="${groupId}"]`);
    if (!groupSection) return null;
    
    // Look for an available variant slot within this specific group
    return groupSection.querySelector('[data-product-bundle-variant][available]');
  }

  createNewVariantInGroup(groupId) {
    // Find the group section in the sidebar - use specific class selector
    const groupSection = this.querySelector(`.product-bundle__item-group[data-group-id="${groupId}"]`);
    if (!groupSection) return null;
    
    // Clone the template and add it to this specific group
    // Items are added in natural DOM order, no CSS order property needed
    const variantElement = this.template.cloneNode(true);
    groupSection.appendChild(variantElement);
    
    return variantElement;
  }

  findExistingVariant(variantId) {
    // Look for an existing variant in the bundle with the same variant ID
    return this.variants.find(variant => 
      !variant.hasAttribute('available') && 
      variant.getAttribute('data-variant-id') === variantId.toString()
    );
  }

  findExistingVariantInGroup(variantId, groupId) {
    // Find the group section in the sidebar - use specific class selector
    const groupSection = this.querySelector(`.product-bundle__item-group[data-group-id="${groupId}"]`);
    if (!groupSection) return null;
    
    // Look for an existing variant with the same variant ID within this specific group
    const groupVariants = Array.from(groupSection.querySelectorAll('[data-product-bundle-variant]'));
    return groupVariants.find(variant => 
      !variant.hasAttribute('available') && 
      variant.getAttribute('data-variant-id') === variantId.toString()
    );
  }

  incrementVariantQuantity(variantElement) {
    const quantityInput = variantElement.querySelector('quantity-input');
    if (quantityInput) {
      // Remove hidden attribute to show quantity controls
      quantityInput.removeAttribute('hidden');
      
      // Get the input element and increment its value
      const input = quantityInput.querySelector('.quantity__input');
      if (input) {
        const currentValue = parseInt(input.value) || 1;
        input.value = currentValue + 1;
        
        // Trigger change event to update totals
        input.dispatchEvent(new Event('change', { bubbles: true }));
        
        // Update group button states after incrementing
        const groupSection = variantElement.closest('[data-group-id]');
        if (groupSection) {
          const groupId = groupSection.getAttribute('data-group-id');
          this.updateGroupButtonStates(groupId);
        }
      }
    }
  }

  getResizedImageSrc(src, size) {
    return `${src}${src.includes("?")?"&":"?"}width=${size}&height=${size}`.replace(/\n|\r|\s/g, "");
  }
}
customElements.define('product-bundle-stepped', ProductBundleStepped);